import React, { useState } from "react";
import {
  Modal,
  Form,
  Button,
  Row,
  Col,
  Alert,
  FloatingLabel
} from "react-bootstrap";
import axios from "axios";
import { API_ENDPOINTS } from '../config/api';

const ConferenceDetailsForm = ({ show, onHide, session, onDetailsSaved }) => {
  const [formData, setFormData] = useState({
    speaker: "",
    speakerTitle: "",
    talkTitle: "",
    talkTime: "",
    location: "",
    image: "",
    zoomLink: "",
    description: "",
    attachments: [] // Only for already-uploaded files
  });
  const [showZoomSeriesNotice, setShowZoomSeriesNotice] = useState(false);
  const [initialZoomLink, setInitialZoomLink] = useState("");
  const [changeLocationForSessionOnly, setChangeLocationForSessionOnly] = useState(false);
  const [changeZoomLinkForSessionOnly, setChangeZoomLinkForSessionOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [deletingIndex, setDeletingIndex] = useState(null);
  const [applyImageToSeries, setApplyImageToSeries] = useState(false);
  // Remove selectedFiles state

  const renderToggleCheckButton = (id, label, checked, onToggle) => (
    <Button
      type="button"
      variant={checked ? "primary" : "outline-secondary"}
      className="w-100 d-flex align-items-center justify-content-start gap-2 text-start"
      onClick={onToggle}
      aria-pressed={checked}
    >
      <input
        id={id}
        type="checkbox"
        className="form-check-input m-0"
        checked={checked}
        onClick={(e) => e.stopPropagation()}
        onChange={onToggle}
      />
      <span>{label}</span>
    </Button>
  );

  React.useEffect(() => {
    if (show && session) {
      setFormData({
        speaker: session.speaker || "",
        speakerTitle: session.speakerTitle || "",
        talkTitle: session.talkTitle || "",
        talkTime: session.talkTime || "",
        location: session.location || session.conference?.location || "",
        image: session.image || session.conference?.image || "",
        zoomLink: session.zoomLink || session.conference?.zoomLink || "",
        description: session.description || "",
        attachments: session.attachments || []
      });
      setInitialZoomLink(session.zoomLink || session.conference?.zoomLink || "");
      setShowZoomSeriesNotice(false);
      setChangeLocationForSessionOnly(false);
      setChangeZoomLinkForSessionOnly(false);
      setUploadError("");
    }
  }, [show, session]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Upload files immediately on selection
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!session || files.length === 0) return;
    setUploading(true);
    setUploadError("");
    try {
      const uploaded = [];
      await Promise.all(files.map(async (file) => {
        const formDataObj = new FormData();
        formDataObj.append('file', file);
        const res = await axios.post(
          `${API_ENDPOINTS.CONFERENCE_SESSIONS}/${session.id}/upload-attachment`,
          formDataObj,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        if (res.data && res.data.attachment) {
          uploaded.push(res.data.attachment);
        }
      }));
      setFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...uploaded]
      }));
    } catch (err) {
      setUploadError(err.response?.data?.error || "Failed to upload file(s)");
    } finally {
      setUploading(false);
    }
  };

  // Remove removeSelectedFile and handleUpload

  const removeUploadedAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
    // Optionally: send a DELETE request to backend to remove the file
  };

  // Upload image immediately on selection
  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!session || files.length === 0) return;
    setUploading(true);
    setUploadError("");
    try {
      const imgFile = files[0];
      const formDataObj = new FormData();
      formDataObj.append('image', imgFile);
      formDataObj.append('applyToSeries', applyImageToSeries ? 'true' : 'false');
      const res = await axios.post(
        `${API_ENDPOINTS.CONFERENCE_SESSIONS}/${session.id}/upload-image`,
        formDataObj,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      if (res.data && res.data.image) {
        setFormData(prev => ({ ...prev, image: res.data.image }));
      }
    } catch (err) {
      setUploadError(err.response?.data?.error || "Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAttachment = async (file, index) => {
    if (!session || typeof file !== 'string') return;
    setDeletingIndex(index);
    setUploadError("");
    try {
      await axios.delete(
        `${API_ENDPOINTS.CONFERENCE_SESSIONS}/${session.id}/attachment`,
        {
          params: { path: file },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      setFormData(prev => ({
        ...prev,
        attachments: prev.attachments.filter((att, i) => i !== index)
      }));
    } catch (err) {
      setUploadError(err.response?.data?.error || "Failed to delete attachment");
    } finally {
      setDeletingIndex(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!session) {
      setError("No session selected");
      return;
    }
    // Frontend validation for required fields
    if (!formData.speaker.trim() || !formData.talkTitle.trim() || !formData.location.trim()) {
      setError("Speaker, Talk Title, and Location are required fields.");
      return;
    }
    try {
      setLoading(true);
      setError("");
      // Only send non-file fields and the list of uploaded attachment paths
      const payload = {
        ...formData,
        applyImageToSeries,
        applyLocationToSeries: !changeLocationForSessionOnly,
        applyZoomLinkToSeries: !changeZoomLinkForSessionOnly,
        attachments: formData.attachments // Only file paths, not File objects
      };
      const response = await axios.put(
        `${API_ENDPOINTS.CONFERENCE_SESSIONS}/${session.id}/details`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      setSuccess("Session details saved successfully!");
      setTimeout(() => {
        onDetailsSaved();
        setFormData({
          speaker: "",
          speakerTitle: "",
          talkTitle: "",
          talkTime: "",
          location: "",
          zoomLink: "",
          description: "",
          attachments: []
        });
        setSuccess("");
      }, 2000);
    } catch (error) {
      console.error("Error saving session details:", error);
      setError(error.response?.data?.error || "Failed to save session details");
    } finally {
      setLoading(false);
    }
  };

  if (!session) return null;
  const conference = session.conference || {};
  // Build date using Y-M-D parts to avoid timezone shifting one day
  let dateStr = '';
  try {
    if (session.date) {
      const [yyyy, mm, dd] = session.date.split('-').map((v) => parseInt(v, 10));
      const safeDate = (yyyy && mm && dd) ? new Date(yyyy, mm - 1, dd) : new Date(session.date);
      dateStr = safeDate.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }
  } catch (_) {
    dateStr = new Date(session.date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header  closeButton className="border-bottom-0 px-4">
        <Modal.Title className="w-50">
          <div className="d-flex flex flex-column justify-content-start align-items-start">
            <span>Add Session Details</span>
            <div className="text-hl"></div>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body className="px-4">
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
          {uploadError && <Alert variant="danger">{uploadError}</Alert>}
          {uploading && <Alert variant="info">Uploading file(s)...</Alert>}

          <div className="mb-3">
            <h6>Conference Information</h6>
            <Row>
              <Col md={6}>
            <p className="mb-0">
              <strong>Conference:</strong> {conference.title || 'Unknown Conference'}<br />
              <strong>Date:</strong> {dateStr}
            </p></Col>
            <Col md={6}>
              <p>
                <strong>Time:</strong> {conference.startTime} - {conference.endTime}<br />
                <strong>Attendance Code:</strong> <code className=" badg bg-primary text-primary px-2 py-1 rounded-2 ">{session.attendanceCode}</code>
              </p>
            </Col>
            </Row>
          </div>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <FloatingLabel controlId="floatingInput" label="Speaker Name" className="mb-3">
                <Form.Control
                  type="text"
                  name="speaker"
                  value={formData.speaker}
                  onChange={handleInputChange}
                  placeholder="Enter speaker name"
                />
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <FloatingLabel controlId="floatingInput" label="Speaker Title" className="mb-3">
                <Form.Control
                  type="text"
                  name="speakerTitle"
                  value={formData.speakerTitle}
                  onChange={handleInputChange}
                  placeholder="Enter speaker title/credentials"
                />
                </FloatingLabel>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <FloatingLabel controlId="floatingInput" label="Talk Title" className="mb-3">
                <Form.Control
                  type="text"
                  name="talkTitle"
                  value={formData.talkTitle}
                  onChange={handleInputChange}
                  placeholder="Enter talk title"
                />
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <FloatingLabel controlId="floatingInput" label="Talk Time" className="mb-3">
                <Form.Control
                  type="time"
                  name="talkTime"
                  value={formData.talkTime}
                  onChange={handleInputChange}
                />
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <FloatingLabel controlId="floatingInput" label="Location" className="mb-3">
                <Form.Control
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="Enter location (e.g., Conference Room A, Auditorium)"
                />
                </FloatingLabel>
                {renderToggleCheckButton(
                  "change-location-session-only",
                  "Change location for this session only",
                  changeLocationForSessionOnly,
                  () => setChangeLocationForSessionOnly((prev) => !prev)
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <FloatingLabel controlId="floatingZoomLink" label="Zoom Link (Optional)" className="mb-3">
                <Form.Control
                  type="url"
                  name="zoomLink"
                  value={formData.zoomLink}
                  onChange={(e) => {
                    handleInputChange(e);
                    setShowZoomSeriesNotice(!changeZoomLinkForSessionOnly);
                  }}
                  onFocus={() => setShowZoomSeriesNotice(!changeZoomLinkForSessionOnly)}
                  onBlur={() => {
                    // Hide notice when not editing and value unchanged
                    if ((formData.zoomLink || "") === (initialZoomLink || "") || changeZoomLinkForSessionOnly) {
                      setShowZoomSeriesNotice(false);
                    }
                  }}
                  placeholder="https://zoom.us/j/..."
                />
                </FloatingLabel>
                {showZoomSeriesNotice && (
                  <Alert variant="info" className="py-2 mb-0">
                    Changes to the Zoom link will be applied to all sessions in this conference series.
                  </Alert>
                )}
                <div className="mt-2">
                  {renderToggleCheckButton(
                    "change-zoom-session-only",
                    "Change Zoom link for this session only",
                    changeZoomLinkForSessionOnly,
                    () => {
                      setChangeZoomLinkForSessionOnly((prev) => {
                        const next = !prev;
                        if (next) {
                          setShowZoomSeriesNotice(false);
                        } else if ((formData.zoomLink || "") !== (initialZoomLink || "")) {
                          setShowZoomSeriesNotice(true);
                        }
                        return next;
                      });
                    }
                  )}
                </div>
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group className="mb-3">
                <FloatingLabel controlId="floatingInput" label="Description" className="mb-3">
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter detailed description of the session" 
                  style={{height: '100px'}}
                />
                </FloatingLabel>
              </Form.Group>
            </Col>
          </Row>

          {/* File upload UI */}
          <Form.Group className="mb-3">
            <Form.Label>Session Image</Form.Label>
            <div className="d-flex align-items-center gap-3">
              <Form.Control
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                disabled={uploading}
              />
              {formData.image && (() => {
                const token = localStorage.getItem('token');
                const url = formData.image.startsWith('/uploads/') ? `${window.location.origin}${formData.image}?token=${token}` : formData.image;
                return (
                  <img src={url} alt="Session" style={{ maxHeight: '64px', borderRadius: '6px' }} />
                );
              })()}
            </div>
            <div className="mt-2">
              {renderToggleCheckButton(
                "apply-image-series",
                "Apply this image to all sessions in this conference series",
                applyImageToSeries,
                () => setApplyImageToSeries((prev) => !prev)
              )}
            </div>
          </Form.Group>

          {/* Attachments */}
          <Form.Group className="mb-3">
            <Form.Label>Attachments</Form.Label>
            <Form.Control
              type="file"
              multiple
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.jpg,.jpeg,.png"
              disabled={uploading}
            />
            <Form.Text className="text-muted">
              Supported formats: PDF, Word, PowerPoint, Excel, Text, Images
            </Form.Text>
          </Form.Group>

          {/* Already uploaded attachments */}
          {formData.attachments.length > 0 && (
            <div className="mb-3">
              <h6>Uploaded Attachments:</h6>
              <ul className="list-unstyled">
                {formData.attachments.map((file, index) => {
                  if (typeof file !== 'string') return null;
                  
                  const handleViewAttachment = async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const url = file.startsWith('http') ? file : 
                        file.startsWith('/uploads/') ? `${window.location.origin}${file}` : 
                        `${window.location.origin}/uploads/${file}`;
                      
                      const response = await fetch(url, {
                        headers: {
                          'Authorization': `Bearer ${token}`,
                          'Content-Type': 'application/octet-stream'
                        }
                      });
                      
                      if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                      }
                      
                      const blob = await response.blob();
                      const viewUrl = window.URL.createObjectURL(blob);
                      window.open(viewUrl, '_blank');
                    } catch (error) {
                      console.error('Error viewing attachment:', error);
                      alert('Failed to view attachment. Please try again.');
                    }
                  };
                  
                  return (
                    <li key={index} className="d-flex justify-content-between align-items-center mb-1">
                      <Button
                        variant="link"
                        className="p-0 text-start"
                        onClick={handleViewAttachment}
                        style={{ textDecoration: 'none' }}
                      >
                        {file.split('/').pop()}
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteAttachment(file, index)}
                        disabled={deletingIndex === index || uploading}
                      >
                        {deletingIndex === index ? 'Deleting...' : 'Remove'}
                      </Button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-top-0 px-4">
          <Button variant="light" onClick={onHide} disabled={loading || uploading}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={loading || uploading}>
            {loading ? "Saving..." : "Save Details"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ConferenceDetailsForm; 